import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import StatusBadge, { getStatusIcon, getStatusFallback } from '../badges/StatusBadge';
import NationalTeamBadge from '../badges/NationalTeamBadge';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    icon: {
        fontSize: 14,
    },
    badgeGroup: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginLeft: theme.spacing(1),
    },
    meta: {
        marginLeft: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: 11,
    },
}));

/**
 * Competition Design System — the reusable "athlete name + status" label.
 * Wherever a status badge is shown next to plain athlete text (roster
 * lists, kata points tables, manual round editors, the draw diff viewer,
 * search pickers), this is the ONE component to render it with — replaces
 * what used to be an almost-identical hand-rolled `{name}{conditional
 * StatusBadge}` block, duplicated across three files.
 *
 * Responsibilities, and nothing else: the athlete's name, the CSS class
 * StatusBadge already resolves from theme tokens, the badge itself, an
 * icon (not color alone — accessibility), a tooltip with the localized
 * status on hover, and an aria-label carrying the same text for
 * screen readers. `CompetitorCard` (a richer card: club/rank/position) is
 * NOT built on this component — it renders its own badge internally,
 * sourcing the SAME StatusBadge/tone data, so there is still one source of
 * truth for the presentation DATA even though there are two rendering call
 * sites for the richer vs. plain-text contexts.
 *
 * `nationalTeam` is a SEPARATE, independent classification (never a
 * CompetitionAttendanceStatus value — see api/docs/ATTENDANCE.md) — it
 * renders alongside the status badge, or alone when `status` is null, using
 * the SAME NationalTeamBadge CompetitorCard renders, so there is one place
 * this visual exists.
 *
 * `rank`/`qualification` are the athlete's Kyu/Dan rank and federation
 * grading category as of the competition date — two SEPARATE, independent
 * classifications from each other and from `status`/`nationalTeam` (distinct
 * backend entities, distinct dictionaries; never conflated). Rendered as a
 * compact "· "-joined secondary line next to the name, mirroring
 * CompetitorCard's own rank/qualification presentation — the ONE place
 * either value is formatted.
 */
const AthleteStatusLabel = props => {
    const { name, status, nationalTeam, rank, qualification, size, className } = props;
    const classes = useStyles();
    const [t] = useTranslation();
    const meta = [rank, qualification].filter(Boolean).join(' · ');

    if (!status && !nationalTeam && !meta) {
        return (
            <span className={className} data-testid='athlete-status-label' data-status='none'>
                {name}
            </span>
        );
    }

    const Icon = status ? getStatusIcon(status) : null;
    const label = status ? t(`competition.status.${status}`, getStatusFallback(status)) : null;

    return (
        <span
            className={[classes.root, className].filter(Boolean).join(' ')}
            data-testid='athlete-status-label'
            data-status={status || 'none'}
            data-national-team={nationalTeam ? 'true' : 'false'}
        >
            {name}
            {meta && (
                <span className={classes.meta} data-testid='athlete-meta'>
                    {meta}
                </span>
            )}
            {status && (
                <Tooltip title={label}>
                    <span className={classes.badgeGroup} aria-label={label}>
                        {Icon && <Icon className={classes.icon} data-testid='athlete-status-icon' />}
                        <StatusBadge status={status} size={size} />
                    </span>
                </Tooltip>
            )}
            {nationalTeam && <NationalTeamBadge size={size} />}
        </span>
    );
};

AthleteStatusLabel.propTypes = {
    // Not .isRequired: callers may render a row before the athlete's name has
    // resolved yet (e.g. a store still loading) — the label degrades to blank.
    name: PropTypes.node,
    /** A StatusBadge key (e.g. 'absent'); null/undefined renders the name alone. */
    status: PropTypes.string,
    nationalTeam: PropTypes.bool,
    /** Kyu/Dan rank at the competition date; null/undefined omits it. */
    rank: PropTypes.string,
    /** Federation grading category at the competition date; null/undefined omits it. */
    qualification: PropTypes.string,
    size: PropTypes.oneOf(['sm', 'md']),
    className: PropTypes.string,
};

AthleteStatusLabel.defaultProps = {
    name: null,
    status: null,
    nationalTeam: false,
    rank: null,
    qualification: null,
    size: 'sm',
    className: null,
};

export default AthleteStatusLabel;
